import React, { useEffect, useMemo, useState } from 'react';
import * as Accordion from '@radix-ui/react-accordion';
import * as Select from '@radix-ui/react-select';
import {
  ComponentRendering,
  Field,
  GetStaticComponentProps,
  Image,
  ImageField,
  useSitecoreContext,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import axios from 'axios';
import { Cheerio, load } from 'cheerio';
import classNames from 'classnames';
import { Element } from 'domhandler';
import { sanitize } from 'isomorphic-dompurify';
import { v4 as uuidv4 } from 'uuid';

import { icon_arrow_right, icon_chevron_down, icon_chevron_up } from '@gn-audio/icons';
import {
  Button,
  calculateDarkerHex,
  ComponentProps,
  JabraIcon,
  nameListToArray,
  resolveColorProperty,
  useFallbackTranslation,
  useJabraTheme,
  withTheme,
} from '@gn-audio/jss-components';
import {
  DEFAULT_LOCALE,
  DocumentItem,
  getLocaleWithRegion,
  getSharedDictionary,
  getSharedProperty,
} from '@gn-audio/jss-core';

import { useSpotTracking } from 'utils/hooks/useSpotTracking';

import styles from './TechSpec.module.css';

type HtmlObject = {
  [key: string]: HtmlContent;
};

type HtmlContent = {
  id?: string;
  class?: string;
  text?: string;
  src?: string;
  alt?: string;
  content?: ParsedHtml;
};

type ParsedHtml = HtmlObject[];

type TechSpecFields = {
  fields: {
    Header: Field<string>;
    Text: Field<string>;
    Image: ImageField;
    GroupIds: Field<string>;
    OptimisedForLabel: Field<string>;
    DocTypes: Field<string>;
    trackingSpotName?: Field<string>;
  };
};

type ParsedTechSpecFields = {
  id: string;
  sectionHeadline: ParsedHtml;
  sectionDetail: ParsedHtml;
};

type TechSpecProps = {
  techSpecFields: TechSpecFields;
  knowledgeBaseApiUrl: string;
  parsedTechSpec: ParsedTechSpecFields[];
} & ComponentProps;

function convertToCamelCase(str: string): string {
  let parts = str.split('__');
  parts = parts.map((part) => part.replace(/-([a-z])/g, (g) => g[1].toUpperCase()));

  for (let i = 1; i < parts.length; i++) {
    parts[i] = parts[i].charAt(0).toUpperCase() + parts[i].slice(1);
  }

  return parts.join('');
}

const JSXElement = (JSXTag: string, props: any, content?: any): React.ReactElement => {
  if (JSXTag === 'img') {
    const { id, key, className, src, alt } = props;
    return <img key={key ?? id} className={className} src={src} alt={alt} />;
  } else {
    return React.createElement(JSXTag, props, content || null);
  }
};

// React throws if a self-closing tag receives children
const SELF_CLOSING_TAGS = /^(br|hr)$/;

function constructJSXRecursive(parsedObject: ParsedHtml): React.ReactElement[] {
  return parsedObject.map((item, index) => {
    const key = Object.keys(item)[0];
    const elData: HtmlContent = item[key];
    const className = elData.class
      ? classNames(
          elData.class
            .split(' ')
            .map((cls) => styles[convertToCamelCase(cls)] || styles.defaultClass)
        )
      : styles.defaultClass;
    let elChildren: Array<React.ReactElement<any, any>> | string = '';
    if (elData.content) {
      const contentArray = Array.isArray(elData.content) ? elData.content : [elData.content];
      elChildren = constructJSXRecursive(contentArray);
    } else if (elData.text) {
      elChildren = elData.text;
    }
    const { class: _, ...restElData } = elData; // Destructure to exclude `class` from `elData`
    // Tags come from cheerio-parsed HTML (already lowercase), render them as native elements.
    const JSXTag = key;
    const reactKey = `${key}-${index}`;
    if (SELF_CLOSING_TAGS.test(key)) {
      return JSXElement(JSXTag, { key: reactKey });
    }
    if (key === 'span' && !elData.content) {
      return JSXElement(JSXTag, {
        key: reactKey,
        ...restElData,
        className,
        dangerouslySetInnerHTML: { __html: sanitize(elData.text ?? '') },
      });
    }
    return JSXElement(JSXTag, { key: reactKey, ...restElData, className }, elChildren);
  });
}

const calculateAccordionItemValue = (itemValue: string): string => {
  return itemValue.includes('batteryWarning') ? 'batteryWarning' : itemValue;
};

const getItemValue = (item: ParsedTechSpecFields): string => {
  const sectionClass = item.sectionHeadline[0][Object.keys(item.sectionHeadline[0])[0]].class;
  return `${item.id}-${sectionClass}`;
};

const TechSpec = ({ techSpecFields, knowledgeBaseApiUrl, parsedTechSpec }: TechSpecProps) => {
  const { t } = useFallbackTranslation();
  const spotRef = useSpotTracking(techSpecFields.fields.trackingSpotName?.value);

  const { sitecoreContext } = useSitecoreContext();
  const locale = getLocaleWithRegion(sitecoreContext.language);
  const themeColors = useJabraTheme();
  const [docs, setDocs] = useState<DocumentItem[]>([]);
  const [hoveredItemIndex, setHoveredItemIndex] = useState('');
  const fieldsData = techSpecFields.fields;

  const backgroundColor = useMemo(() => {
    const defaultBackgroundColor = '#FFFFFF';
    return themeColors?.backgroundColor
      ? (resolveColorProperty(themeColors.backgroundColor) ?? defaultBackgroundColor)
      : defaultBackgroundColor;
  }, [themeColors]);
  const foregroundColor = themeColors?.foregroundColor || '#000000';
  const mainColor = useMemo(() => calculateDarkerHex(backgroundColor, 0.1), [backgroundColor]);
  const header = useMemo(() => getSharedDictionary(fieldsData, 'Header'), [fieldsData]);
  const image = useMemo(() => getSharedProperty(fieldsData, 'Image'), [fieldsData]);
  const optimisedForLabel = useMemo(
    () => getSharedProperty(fieldsData, 'OptimisedForLabel'),
    [fieldsData]
  );
  const docTypes = useMemo(
    () => getSharedProperty(fieldsData, 'DocTypes')?.value || '',
    [fieldsData]
  );

  const groupIds = useMemo(() => {
    const groupIdsObject: {
      [key: string]: { value: string };
    } = { GroupIds: { value: getSharedProperty(fieldsData, 'GroupIds')?.value || '' } };

    return nameListToArray(groupIdsObject || [])[0];
  }, [fieldsData]);

  const [selectValue, setSelectValue] = useState<string>(Object.keys(groupIds)[0]);
  const [selectOpen, setSelectOpen] = useState(false);
  const [shouldForceMount, setShouldForceMount] = useState(true);

  useEffect(() => {
    setShouldForceMount(false);
  }, []);

  useEffect(() => {
    async function getDocs(groupId: string) {
      try {
        const response = await axios.get(
          `${knowledgeBaseApiUrl}/group/${groupId}/documents?marketLocale=${locale}`
        );

        if (response.status !== 200 || !response.data.length) {
          console.warn('No documents found or invalid response.');
          setDocs([]);
          return;
        }

        const documents: { [name: string]: DocumentItem } = {};

        const processDocument = (item: DocumentItem) => {
          const types = docTypes.split(',').map((type) => type.trim());
          if (types.includes(item.documentType || '')) {
            const languageCodeLowerCased = item.languageCode?.toLowerCase();
            if (!languageCodeLowerCased) return;

            if (languageCodeLowerCased.includes(locale.toLowerCase())) {
              documents[item.documentType || ''] = item;
            } else if (
              languageCodeLowerCased.includes(DEFAULT_LOCALE.toLowerCase()) &&
              !documents[item.documentType || '']
            ) {
              documents[item.documentType || ''] = item;
            }
          }
        };

        response.data.forEach((item: DocumentItem) => processDocument(item));
        setDocs(Object.values(documents));
      } catch (error) {
        console.error('Failed to fetch documents:', error);
        setDocs([]);
      }
    }

    if (!!Object.keys(groupIds).length) {
      setDocs([]);
      getDocs(selectValue);
    }
  }, [groupIds, selectValue, locale, docTypes, knowledgeBaseApiUrl]);

  const VariantSelect = () => (
    <div className="w-full">
      <Select.Root
        open={selectOpen}
        onOpenChange={(open) => setSelectOpen(open)}
        defaultValue="empty"
        value={selectValue}
        onValueChange={(value) => setSelectValue(value)}
      >
        <Select.Trigger
          //its hack to disable aria-controls by radix as it adds id that is not available on page until dropdown opened and IBM complains
          aria-controls={''}
          aria-label="Optimised variants"
          className="flex w-full items-center justify-between rounded-xl border border-gray-200 bg-white pb-3 pl-4 pr-4 pt-3 text-gray-700"
        >
          <Select.Value>
            {t(`system.${groupIds[selectValue]}`)
              ? t(`system.${groupIds[selectValue]}`)
              : groupIds[selectValue]}
          </Select.Value>
          {!selectOpen ? (
            <Select.Icon asChild>
              <JabraIcon className="icon h-3" Icon={icon_chevron_down} />
            </Select.Icon>
          ) : (
            <Select.Icon asChild>
              <JabraIcon className="icon h-3" Icon={icon_chevron_up} />
            </Select.Icon>
          )}
        </Select.Trigger>
        <Select.Portal>
          <Select.Content
            position="popper"
            className={classNames(
              styles.techSpecsVariantSelect,
              'overflow-hidden rounded-xl border border-gray-200 bg-white pb-3 pl-4 pr-4 pt-3 shadow-[0px_10px_38px_-10px_rgba(22,_23,_24,_0.35),0px_10px_20px_-15px_rgba(22,_23,_24,_0.2)]'
            )}
          >
            <Select.Viewport>
              {Object.keys(groupIds).map((key) => (
                <Select.Item
                  key={key}
                  value={key}
                  className="pl-1 text-gray-700 hover:cursor-pointer hover:bg-gray-800 hover:text-white"
                >
                  <Select.ItemText>
                    {t(`system.${groupIds[key]}`) ? t(`system.${groupIds[key]}`) : groupIds[key]}
                  </Select.ItemText>
                </Select.Item>
              ))}
            </Select.Viewport>
          </Select.Content>
        </Select.Portal>
      </Select.Root>
    </div>
  );

  return (
    <div
      ref={spotRef}
      data-wt-spot={techSpecFields.fields.trackingSpotName?.value}
      className="bg-theme container-fluid"
    >
      {parsedTechSpec.length ? (
        <div className={classNames(styles.techSpecsSpot, 'container')}>
          <h2 className="heading-2 mb-12 text-center md:mb-24">{header?.value}</h2>
          <div className="flex flex-col items-center justify-center">
            <Accordion.Root
              className={styles.techSpecsList}
              type="multiple"
              defaultValue={['batteryWarning']}
            >
              {parsedTechSpec.map((item) => (
                <Accordion.Item
                  key={item.id}
                  value={calculateAccordionItemValue(getItemValue(item))}
                  className={styles.techSpecsItem}
                >
                  <Accordion.Trigger className={styles.techSpecsListSection}>
                    <div
                      className={styles.techSpecsListSectionContent}
                      style={
                        mainColor && hoveredItemIndex === item.id
                          ? { backgroundColor: calculateDarkerHex(mainColor, 0.05) }
                          : { backgroundColor: mainColor }
                      }
                      onMouseOver={() => {
                        setHoveredItemIndex(item.id);
                      }}
                      onMouseOut={() => {
                        setHoveredItemIndex('');
                      }}
                    >
                      {constructJSXRecursive(item.sectionHeadline)}
                      <div className={styles.sectionChevron}>
                        <JabraIcon Icon={icon_chevron_down} style={{ color: foregroundColor }} />
                      </div>
                    </div>
                  </Accordion.Trigger>
                  <Accordion.Content
                    {...(shouldForceMount ? { forceMount: true } : {})}
                    className={classNames(
                      styles.techSpecsContent,
                      shouldForceMount && styles.techSpecsContentForceMount
                    )}
                  >
                    <div style={{ backgroundColor: mainColor }}>
                      {constructJSXRecursive(item.sectionDetail)}
                    </div>
                  </Accordion.Content>
                </Accordion.Item>
              ))}
            </Accordion.Root>
            <div className="w-full xs:mt-8 sm:w-4/5 xl:w-full">
              <div className="flex flex-col flex-wrap md:flex-row md:justify-between">
                {docs.length > 0 && (
                  <div className="flex flex-col md:items-start lg:basis-5/12">
                    <div className="mb-4 text-[1.5rem] font-bold">{t('system.Download')}</div>
                    {Object.keys(groupIds).length > 1 && (
                      <>
                        <div className="mt-4 text-lg font-bold xs:mt-8">
                          {optimisedForLabel?.value}
                        </div>
                        <div className="mb-6 mt-2 w-auto lg:min-w-[16.5rem]">
                          <VariantSelect />
                        </div>
                      </>
                    )}
                    {docs.map((doc) => (
                      <div
                        className="mb-8"
                        key={`${doc.file}-${doc?.fileSize}-${doc.documentType}`}
                      >
                        <Button
                          href={doc?.fileUrl}
                          asLink
                          download={doc?.file}
                          variant={'secondary'}
                          size={'m'}
                          iconPlacement="right"
                          icon={<JabraIcon Icon={icon_arrow_right} />}
                          className="block w-auto"
                          aria-label={`${t(`system.${doc.documentType}`) || doc.documentType}, ${t('system.DownloadPDF') || 'download PDF'}`}
                          data-wt-name="DownloadButton"
                        >
                          {t(`system.${doc.documentType}`)
                            ? t(`system.${doc.documentType}`)
                            : doc.documentType}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                <Image
                  field={image?.value}
                  className="max-w-[18rem] lg:basis-5/12 lg:justify-self-end"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default withTheme({ legacyBackgroundFieldName: 'BackgroundColorShared' })(
  withDatasourceCheck()<TechSpecProps>(TechSpec)
);

export const getStaticProps: GetStaticComponentProps = async (rendering: ComponentRendering) => {
  try {
    const dataSource = rendering as unknown as TechSpecFields;
    const techSpecHtml = getSharedProperty(dataSource.fields, 'Text');
    const knowledgeBaseApiUrl = process.env.KNOWLEDGEBASE_API_BASE_URL;

    const $ = load(techSpecHtml?.value || '');
    const parsedTechSpec: ParsedTechSpecFields[] = [];

    const createObject = (element: Cheerio<Element>): ParsedHtml => {
      const result: ParsedHtml = [];

      element.each((_index, el) => {
        const uniqueId = uuidv4();
        const key = el.tagName;
        const classes = $(el).attr('class') || '';
        const children = $(el).children();

        if (key === 'img') {
          const imgSrc = $(el).attr('src') || '';
          const altText = $(el).attr('alt') || '';

          result.push({
            [key]: {
              id: uniqueId,
              class: classes,
              src: imgSrc,
              alt: altText,
            },
          });
        } else if (key === 'ul') {
          const ulContent: ParsedHtml = [];

          children.each((_liIndex, liEl) => {
            if (liEl.tagName === 'li') {
              const liContent = $(liEl).children().length
                ? createObject($(liEl).children())
                : [{ span: { text: $(liEl).text() || '' } }];
              ulContent.push({
                li: {
                  id: uuidv4(),
                  class: $(liEl).attr('class') || '',
                  content: liContent,
                },
              });
            }
          });

          result.push({
            [key]: {
              id: uniqueId,
              class: classes,
              content: ulContent,
            },
          });
        } else if (key == 'span') {
          const content = [{ [key]: { text: $(el).html() || '' } }];

          result.push({
            [key]: {
              id: uniqueId,
              class: classes,
              content: content,
            },
          });
        } else {
          const content =
            children.length > 0
              ? createObject(children)
              : [{ [key]: { text: $(el).text() || '' } }];

          result.push({
            [key]: {
              id: uniqueId,
              class: classes,
              content: content,
            },
          });
        }
      });
      return result;
    };

    $('.section__headline').each((_index, element) => {
      const sectionHeadline = $(element) || [];
      if (sectionHeadline && sectionHeadline.length > 0) {
        if (sectionHeadline.parent().hasClass('battery-warning')) {
          sectionHeadline.addClass('batteryWarning');
          $(element)
            .next()
            .find('ul')
            .find('li')
            .contents()
            .each(function () {
              if (this.type === 'tag') {
                $(this).replaceWith($(this).contents());
              }
            });
        }
      }

      const sectionDetail = $(element).next() || [];

      parsedTechSpec.push({
        id: uuidv4(),
        sectionHeadline:
          sectionHeadline && sectionHeadline.length > 0 ? createObject(sectionHeadline) : [],
        sectionDetail: sectionDetail && sectionDetail.length > 0 ? createObject(sectionDetail) : [],
      });
    });

    return {
      techSpecFields: dataSource,
      parsedTechSpec: parsedTechSpec,
      knowledgeBaseApiUrl: knowledgeBaseApiUrl,
    };
  } catch (e) {
    console.log(e);
    return {};
  }
};
